import { ORDER_STATUS_LABELS, SERVICE_MODEL_LABELS } from '../../core/api/api.types';
import type { LiveOrder } from './my-cases.types';

export interface OrderSummaryLine {
  label: string;
  value: string;
}

const PROVIDER_ASSIGNMENT_LABELS: Record<string, string> = {
  unassigned: 'Unassigned',
  unknown: 'Unknown',
  other_in_notes: 'Other — in notes',
};

function formatWhen(order: LiveOrder): string {
  if (!order.scheduled_at) return 'Not scheduled';
  const d = new Date(order.scheduled_at);
  if (Number.isNaN(d.getTime())) return 'Not scheduled';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: order.scheduled_timezone ?? undefined,
    }).format(d);
  } catch {
    // Unknown/invalid timezone string — fall back to the runtime zone.
    return d.toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' });
  }
}

function formatProvider(order: LiveOrder): string {
  if (order.provider_assignment_type === 'assigned') {
    return order.providers?.name ?? 'Assigned';
  }
  return PROVIDER_ASSIGNMENT_LABELS[order.provider_assignment_type] ?? 'Unassigned';
}

function formatCad(cents: number): string {
  return (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

/**
 * Short, human-readable digest of an order built entirely from its own fields —
 * no backend call, no LLM. Always reflects the current order state.
 */
export function buildOrderSummary(order: LiveOrder): OrderSummaryLine[] {
  const lines: OrderSummaryLine[] = [
    { label: 'Service', value: order.service_name || order.category || '—' },
    {
      label: 'Type',
      value: (SERVICE_MODEL_LABELS as Record<string, string>)[order.service_model] ?? order.service_model,
    },
    { label: 'When', value: formatWhen(order) },
    { label: 'Where', value: order.address || order.city_code || '—' },
    { label: 'Provider', value: formatProvider(order) },
  ];

  if (order.quote_amount_cents !== null && order.quote_amount_cents !== undefined) {
    lines.push({ label: 'Quote', value: formatCad(order.quote_amount_cents) });
  }

  lines.push({
    label: 'Status',
    value: (ORDER_STATUS_LABELS as Record<string, string>)[order.status] ?? order.status,
  });

  return lines;
}
